import React, { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { arr } from '@/features/shared/lib/utils';

// Presentational + navigational component for a college block with its departments
export default function DepartmentsCard({ college }) {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();

  const code = college?.value ?? college?.category_code;
  const departments = arr(college?.children);

  // College 대표 컬러(hex) → 학과 카드에 파스텔 톤으로 적용
  const COLLEGE_BASE_COLORS = {
    // provided palette
    'College of Social Sciences': '#004282',
    '사회과학대학': '#004282',
    'College of Education': '#00B9F2',
    '사범대학': '#00B9F2',
    'College of Economics and International Trade': '#6B3916',
    '경제통상대학': '#6B3916',
    'College of Business Administration': '#8C3219',
    '경영대학': '#8C3219',
    'College of Pharmacy': '#778A38',
    '약학대학': '#778A38',
    'College of Human Ecology': '#FBEE38',
    '생활과학대학': '#FBEE38',
    'College of Arts': '#005BAA',
    '예술대학': '#005BAA',
    'College of Nursing': '#FFB07B',
    '간호대학': '#FFB07B',
    'College of Nanoscience and Nanotechnology': '#EEC718',
    '나노과학기술대학': '#EEC718',
    'College of Life Sciences and Natural Resources': '#EC008C',
    '생명자원과학대학': '#EC008C',
    'College of Medicine': '#00A54D',
    '의과대학': '#00A54D',
    'College of Biomedical Informatics': '#900020',
    '정보의생명공학대학': '#900020',
    'Graduate School of International Studies': '#6E0000',
    '국제전문대학원': '#6E0000',
    'School of Dentistry': '#B0ACD5',
    '치의학전문대학원': '#B0ACD5',
    'School of Korean Medicine': '#1A9A91',
    '한의학전문대학원': '#1A9A91',
    'Law School': '#4EC9F5',
    '법학전문대학원': '#4EC9F5',
    'Graduate School of Data Science': '#00A052',
    '데이터사이언스전문대학원': '#00A052',
    'College of Natural Sciences': '#B97C24',
    '자연과학대학': '#B97C24',
    'College of Engineering': '#FFA500',
    '공과대학': '#FFA500',
  };

  const hexToRgba = (hex, alpha = 0.15) => {
    const h = String(hex || '').replace('#', '').trim();
    if (h.length !== 6) return null;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    if ([r, g, b].some((n) => Number.isNaN(n))) return null;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const collegeNameEn = String(college?.category_name_en || '').trim();
  const collegeNameKo = String(college?.category_name || '').trim();
  const isHumanities = collegeNameEn === 'College of Humanities' || (typeof collegeNameKo === 'string' && collegeNameKo.includes('인문대학'));
  const baseHex = COLLEGE_BASE_COLORS[collegeNameEn] || COLLEGE_BASE_COLORS[collegeNameKo] || null;
  const deptBgStyle = !isHumanities && baseHex ? { backgroundColor: hexToRgba(baseHex, 0.07) } : undefined;

  const onCollegeClick = useCallback((e) => {
    // SPA: set the search param `college` to this code (push navigation)
    try {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    } catch { }
    if (!code) return;
    const next = new URLSearchParams(sp);
    next.set('college', String(code));
    setSp(next, { replace: false });
  }, [code, sp, setSp]);

  const onDeptClick = useCallback((dept, e) => {
    try {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    } catch { }
    const id = dept?.category_code ?? dept?.category_code ?? dept?.id;
    if (!id) return;
    navigate(`/departments/${encodeURIComponent(id)}`);
    try {
      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        setTimeout(() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' }), 0);
      }
    } catch { }
  }, [navigate]);

  return (
    <div className="p-6 bg-white rounded-[12px] border border-[#E4E8F1] rounded-[12px] lg:border-none lg:p-10 xl:flex xl:gap-5">
      <div
        className="pb-4 border-b border-b-[#E4E8F1] xl:hover:shadow-[0px_4px_30px_0px_rgba(0,0,0,0.1)] transitionn-shadow duration-300 xl:w-[400px] xl:shrink-0 xl:pb-0 xl:relative xl:overflow-hidden xl:rounded-[12px] cursor-pointer min-h-[160px]"
        onClick={onCollegeClick}
      >
        <div
          className="flex justify-between mb-[15px] xl:absolute xl:inset-0 xl:z-10 xl:bg-gradient-to-b from-[#F3F5F9] from-50% to-transparent xl:mb-0 xl:p-10 xl:flex-col xl:justify-start"
        >
          <p className="text-[16px] font-bold leading-[130%] lg:text-[20px]">{college?.category_name_en}</p>
          {/* 한글명과 동일 데이터이므로 보조 텍스트 유지 */}
          <p className="hidden xl:block text-[16px] text-[#6C7687] font-bold mt-[7px]">{college?.category_name}</p>
          <img src={rmVars.themeUrl + 'images/arr_right.png'} className="w-4 h-4 xl:hidden" alt="arr" />
        </div>

        <div className="w-full h-[82px] overflow-hidden rounded-[12px] xl:h-full xl:absolute xl:top-0 xl:left-0">
          <img src={college?.thumbnail || (rmVars.themeUrl + 'images/college-humanity.png')} className="w-full h-full object-cover" alt="college" />
        </div>
      </div>

      <ul className="flex-1 flex flex-wrap gap-4 w-full mt-6 md:gap-5 xl:mt-0 self-start">
        {departments.map((dept) => (
          <li
            key={dept?.category_id}
            className={`w-[calc((100%-16px)/2)] p-4 rounded-[12px] leading-[140%] font-bold md:w-[calc((100%-40px)/3)] lg:px-8 lg:py-6 lg:hover:shadow-[0px_4px_30px_0px_rgba(0,0,0,0.1)] transitionn-shadow duration-300 xl:w-[calc((100%-60px)/4)] cursor-pointer ${deptBgStyle ? '' : 'bg-[#F3F5F9]'}`}
            style={deptBgStyle}
            onClick={(e) => onDeptClick(dept, e)}
          >
            <div className="block w-full text-left">
              <p className="text-[14px] lg:text-[16px]">{dept?.category_name_en}</p>
              <p className="mt-3 text-[12px] text-[#6C7687] lg:text-[13px]">{dept?.category_name}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
